import axios from '@/lib/axios';
import Cookies from 'js-cookie';

const AUTH_COOKIE_NAME = 'auth_token';
const ADMIN_AUTH_COOKIE_NAME = 'admin_auth_token';
const COOKIE_EXPIRY_DAYS = 365; // 1 year

export interface ActiveSession {
    id: number;
    ip_address: string | null;
    device_type: string | null;
    browser: string | null;
    os: string | null;
    created_at: string;
    last_used_at: string;
    expires_at: string | null;
    is_current: boolean;
}

export interface RegisterData {
    email: string
    password: string
    full_name: string
    phone?: string
    /** Cloudflare Turnstile token, verified server-side before the account is made. */
    turnstile_token?: string | null
}

export interface LoginData {
    email: string
    password: string
    /** Verified before any credential check, so a spray is stopped up front. */
    turnstile_token?: string | null
}

export const authService = {
    // Register: Create account and request OTP
    register: async (data: RegisterData) => {
        const response = await axios.post('/auth/register', data);
        return response.data;
    },

    // Verify OTP: Activate account and get token
    verifyOtp: async (email: string, otp: string) => {
        const response = await axios.post('/auth/verify-otp', { email, otp });
        const { access_token } = response.data;
        if (access_token) {
            authService.setToken(access_token);
        }
        return response.data;
    },

    // Login: Get token via password (or triggering 2FA)
    login: async (data: LoginData) => {
        const response = await axios.post('/auth/login', data);
        if (response.data.requires_2fa) {
            return response.data; // { requires_2fa: true, email: string, message: string }
        }

        const { access_token } = response.data;
        if (access_token) {
            authService.setToken(access_token);
        }
        return response.data;
    },

    // Verify 2FA Login
    verify2FALogin: async (email: string, otp: string) => {
        const response = await axios.post('/auth/verify-2fa-login', { email, otp });
        const { access_token } = response.data;
        if (access_token) {
            authService.setToken(access_token);
        }
        return response.data;
    },

    // Google Login
    googleLogin: async (code: string, redirectUri?: string) => {
        const response = await axios.post('/auth/google/login', {
            code,
            ...(redirectUri && { redirect_uri: redirectUri }),
        });
        const { access_token } = response.data;
        if (access_token) {
            authService.setToken(access_token);
        }
        return response.data;
    },

    // Forgot Password: Request OTP
    forgotPassword: async (email: string) => {
        const response = await axios.post('/auth/forgot-password', { email });
        return response.data;
    },

    // Verify Reset OTP: Get reset token
    verifyResetOtp: async (email: string, otp: string) => {
        const response = await axios.post('/auth/verify-reset-otp', { email, otp });
        // Returns { access_token, token_type }
        return response.data;
    },

    // Reset Password: Set new password with token
    resetPassword: async (token: string, new_password: string) => {
        const response = await axios.post('/auth/reset-password', { token, new_password });
        return response.data;
    },

    // Change Password (Logged in)
    changePassword: async (current_password: string, new_password: string) => {
        const response = await axios.post('/users/me/password', { current_password, new_password });
        return response.data;
    },

    // Get Current User Profile
    getCurrentUser: async () => {
        const response = await axios.get('/users/me');
        return response.data;
    },

    // Get Public User Profile (no auth required)
    getPublicProfile: async (userId: number) => {
        const response = await axios.get(`/users/public/${userId}`);
        return response.data;
    },

    // Update Current User Profile
    updateProfile: async (data: { full_name?: string; bio?: string; avatar_url?: string }) => {
        const response = await axios.put('/users/me', data);
        return response.data;
    },

    // Sync Profile from SSO
    syncProfile: async () => {
        const response = await axios.post('/users/me/sync');
        return response.data;
    },

    // Redeem Referral Code
    redeemReferral: async (code: string) => {
        const response = await axios.post('/users/me/redeem-referral', { code });
        return response.data;
    },
    // Impersonation
    impersonate: async (userId: number) => {
        const response = await axios.post(`/users/${userId}/impersonate`);
        return response.data;
    },

    startImpersonation: (token: string) => {
        const currentToken = Cookies.get(AUTH_COOKIE_NAME);
        if (currentToken) {
            Cookies.set(ADMIN_AUTH_COOKIE_NAME, currentToken, {
                expires: 1,
                secure: window.location.protocol === 'https:',
                path: '/'
            });
        }
        authService.setToken(token);
        window.location.href = '/dashboard';
    },

    stopImpersonation: () => {
        const adminToken = Cookies.get(ADMIN_AUTH_COOKIE_NAME);
        if (adminToken) {
            authService.setToken(adminToken);
            Cookies.remove(ADMIN_AUTH_COOKIE_NAME);
            window.location.reload();
        }
    },

    isImpersonating: () => {
        return !!Cookies.get(ADMIN_AUTH_COOKIE_NAME);
    },


    setToken: (token: string) => {
        Cookies.set(AUTH_COOKIE_NAME, token, {
            expires: COOKIE_EXPIRY_DAYS,
            secure: window.location.protocol === 'https:',
            path: '/'
        });
    },

    getToken: () => {
        return Cookies.get(AUTH_COOKIE_NAME);
    },

    logout: async () => {
        try {
            await axios.post('/auth/logout');
        } catch {
            // ignore — remove cookies regardless
        }
        Cookies.remove(AUTH_COOKIE_NAME);
        Cookies.remove(ADMIN_AUTH_COOKIE_NAME);
        window.location.href = '/';
    },

    // Active Sessions
    getSessions: async (): Promise<{ sessions: ActiveSession[]; total: number }> => {
        const response = await axios.get('/auth/sessions');
        return response.data;
    },

    revokeSession: async (sessionId: number): Promise<void> => {
        await axios.delete(`/auth/sessions/${sessionId}`);
    },

    revokeOtherSessions: async (): Promise<{ message: string; count: number }> => {
        const response = await axios.delete('/auth/sessions');
        return response.data;
    },

    checkStatus: async () => {
        const response = await axios.get('/auth/status');
        return response.data; // { is_authenticated: boolean }
    }
};
